import Service, { minutes, seconds } from './Service';
import type TokenizedApi from '../TokenizedApi';
import {
  restApiVault,
  restApiLockbox,
  schemaLockboxBalanceList,
  restApiInstrumentDetails,
  restApiNetEquityHistory,
  restApiCurrencyExchangeRate,
  TLockbox,
  TInstrumentBalance,
  TCurrencyExchangeRate,
  TVault,
  restApiCurrencyExchangeRates,
  restApiProfile,
  restApiEntity,
} from './TokenizedApiModelEntities';
import * as schema from '../utils/schema';
import {
  formatInstrumentTypeName,
  INSTRUMENT_TYPE_ORDER,
  isInstrumentExpired,
  TInstrumentOptions,
  TContract,
  Instrument,
} from '../utils/instruments';
import { urlPath } from '../utils/utils';
import { TSdkIntlShape, TokenizedResponseInvalidError } from '../TokenizedApi';
import ExchangeRates from '../utils/ExchangeRates';

export interface TBalancesQueryOptions {
  profileId?: string;
  /**
   * The id of the vault to query for balances. The id
   * of the user’s primary vault can be obtained via
   * {@linkcode usePrimaryVault | usePrimaryVault()?.id}.
   */
  vaultId?: string;
  /**
   * Asset/liability filtering: Set `includeLiabilities: true` to only include
   * instruments created by the current entity. Set `includeLiabilities: false` to
   * only include instruments issued to the current entity by another entity (assets),
   * Omit `includeLiabilities` to include assets and liabilities.
   */
  includeLiabilities?: boolean;
  /**
   * Active/inactive filtering: Set `includeInactive: true` to only include
   * instruments that are inactive or expired. Set `includeInactive: false`
   * to only include instruments that are active and not expired.
   * Omit `includeInactive` to include active and inactive instruments.
   */
  includeInactive?: boolean;
  /**
   * Currency/token filtering: Set `includeMoney: true` to only include currency
   * instruments. Set `includeMoney: false` to only include non-currency
   * (token) instruments. Omit `includeMoney` to include both types of instrument.
   */
  includeMoney?: boolean;

  /**
   * Zero balance filtering. Set `removeZeroBalances: true` to exclude all
   * zero balances, `removeZeroBalances: false` to include all zero balances.
   * Otherwise the default behavior is to remove zero balances for assets, but
   * to include zero-balance liabilities.
   */
  removeZeroBalances?: boolean;
  /**
   * Optional list of instrument IDs to always include,
   * regardless of other filters.
   */
  alwaysAddInstrumentIds?: string[];

  sortOrder?: (keyof typeof balanceOrders)[];
}

export interface TFilteredBalance {
  instrumentId?: string;
  instrumentName?: string | null;
  instrumentType: {
    code?: string;
    formatted?: string;
  };
  instrument?: Instrument;
  contract?: {
    address?: string;
    formatted?: string;
  };
  issuer?: {
    entityId?: string;
    formatted?: string;
  };
  isActive: boolean;
  isExpired: boolean;
  isLiability: boolean;
  quantities: {
    balance?: TInstrumentOptions;
    available?: TInstrumentOptions;
    reserved?: TInstrumentOptions;
    authorizedQuantity?: TInstrumentOptions;
    issuedLiability?: TInstrumentOptions;
  };
}

export interface TTransformedLockbox extends TLockbox {
  lastModifiedTimestamp?: number;
  controlArrangementCanBeModified: boolean;
  formatted: {
    formatControlArrangement?: (intl: TSdkIntlShape) => string;
  };
}

export interface TDisplayLockbox extends TLockbox {
  formatted: TTransformedLockbox['formatted'] & {
    controlArrangement?: string;
  };
}

export const balanceOrders = {
  instrumentTypeGroup: (a: TFilteredBalance, b: TFilteredBalance) => {
    const aType = a?.instrumentType?.code || '';
    const bType = b?.instrumentType?.code || '';
    let aGroup = INSTRUMENT_TYPE_ORDER.indexOf(aType);
    if (aGroup === -1) {
      aGroup = INSTRUMENT_TYPE_ORDER.length;
    }
    let bGroup = INSTRUMENT_TYPE_ORDER.indexOf(bType);
    if (bGroup === -1) {
      bGroup = INSTRUMENT_TYPE_ORDER.length;
    }
    return aGroup - bGroup;
  },
  instrumentType: (a: TFilteredBalance, b: TFilteredBalance) => {
    const aType = a?.instrumentType?.code || '';
    const bType = b?.instrumentType?.code || '';
    return aType?.localeCompare?.(bType);
  },
  instrumentName: (a: TFilteredBalance, b: TFilteredBalance) => {
    const aType = a?.instrumentName || '';
    const bType = b?.instrumentName || '';
    return aType?.localeCompare?.(bType);
  },
};

/** @category SDK Modules */
class Treasury extends Service {
  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);
  }

  vaultsQuery = this.makeQuery(
    (query, profileId?: string) => ({
      ...query,
      // Vaults hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
      enabled: query.enabled && !!profileId,
    }),
    async (context, profileId?: string) => {
      if (!profileId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'vaults'),
      );
      const normalized = schema.normalizeResponse(
        'Query profile vaults',
        response?.data?.data,
        schema.arrayOf(restApiVault),
      );

      const idMap =
        (normalized.modelEntities.vault as Map<string, TVault>) || new Map();

      return {
        vaults: normalized.normalizedValue,
        idMap,
      };
    },
  );

  primaryVaultQuery = this.makeQuery(
    (query, profileId?: string) => ({
      ...query,
      // Vaults hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
      enabled: query.enabled && !!profileId,
    }),
    async (context, profileId?: string) => {
      if (!profileId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'vaults'),
      );
      const normalized = schema.normalizeResponse(
        'Query profile vaults',
        response?.data?.data,
        schema.arrayOf(restApiVault),
      );

      const idMap = normalized.modelEntities.vault as Map<string, TVault>;
      let primaryVault = normalized.normalizedValue?.[0];

      if (
        normalized.normalizedValue?.length &&
        normalized.normalizedValue?.length > 1
      ) {
        const profileResponse = await this.tokenizedApi.endpoints.get(
          urlPath('profiles', profileId),
        );
        const profile = schema.normalizeResponse(
          'Query profile',
          profileResponse?.data?.data,
          restApiProfile,
        ).normalizedValue;
        if (profile?.entityId) {
          const entityResponse = await this.tokenizedApi.endpoints.get(
            urlPath('profiles', profileId, 'entities', profile.entityId),
          );
          const entity = schema.normalizeResponse(
            'Query entity',
            entityResponse?.data?.data,
            restApiEntity,
          ).normalizedValue;
          if (entity?.primaryVaultId && idMap.has(entity.primaryVaultId)) {
            primaryVault = idMap.get(entity.primaryVaultId);
          }
        }
      }

      return primaryVault;
    },
  );

  vaultQuery = this.makeQuery(
    (query, profileId?: string, vaultId?: string) => ({
      ...query,
      // Vaults hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
      enabled: query.enabled && !!profileId && !!vaultId,
    }),
    async (context, profileId?: string, vaultId?: string) => {
      if (!profileId || !vaultId) {
        return null;
      }
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'vaults', vaultId),
      );
      return schema.normalizeResponse(
        'Query vault',
        response?.data?.data,
        restApiVault,
      ).normalizedValue;
    },
  );

  // Note that this query retrieves every lockbox the user has access to,
  // all vaults for every entity. Each lockbox specifies the ID of its entity
  // and vault, which can be used to filter if necessary
  lockboxesQuery = this.makeQuery(
    {
      // Lockboxes hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
    },
    async () => {
      const idMap = new Map<string, TTransformedLockbox>();
      const profilesData =
        await this.tokenizedApi.entities.profilesQuery.fetch();
      const personalProfile = profilesData.personalProfile;
      const lockboxes: TTransformedLockbox[] = (
        await Promise.all(
          (profilesData?.allProfiles || []).map(async (profile) => {
            const profileId = profile.id;
            const isPersonalProfile = profile === personalProfile;

            const vaults =
              (await this.vaultsQuery.fetch(profileId))?.vaults || [];
            return await Promise.all(
              vaults.map(async ({ id: vaultId }) => {
                const response = await this.tokenizedApi.endpoints.get(
                  urlPath(
                    'profiles',
                    profileId,
                    'vaults',
                    vaultId,
                    'lockboxes',
                  ),
                );
                const normalized = schema.normalizeResponse(
                  'Query vault lockboxes',
                  response?.data?.data,
                  schema.arrayOf(restApiLockbox),
                );

                const transformedLockboxes = (
                  normalized.normalizedValue || []
                ).map((lockbox) => {
                  const canSignInLockbox =
                    isPersonalProfile ||
                    (!!personalProfile &&
                      lockbox?.signers.some(
                        ({ handle }) => handle === personalProfile.handle,
                      ));

                  return {
                    ...lockbox,
                    lastModifiedTimestamp: lockbox?.signers?.reduce(
                      (latestTimestamp: number | undefined, signer) => {
                        const signerTimestamp =
                          signer?.dateAccepted || signer?.dateRejected;
                        if (
                          signerTimestamp &&
                          (!latestTimestamp ||
                            signerTimestamp > latestTimestamp)
                        ) {
                          return signerTimestamp;
                        }
                        return latestTimestamp;
                      },
                      undefined,
                    ),
                    controlArrangementCanBeModified:
                      !isPersonalProfile && canSignInLockbox,
                    formatted: {
                      formatControlArrangement:
                        this.tokenizedApi.activity.makeFormatControlArrangement(
                          lockbox,
                          isPersonalProfile,
                        ),
                    },
                  };
                });

                for (const lockbox of transformedLockboxes) {
                  idMap.set(lockbox.id, lockbox);
                }
                return transformedLockboxes;
              }),
            );
          }),
        )
      )
        .flat(2)
        .sort(
          (a, b) =>
            (b.lastModifiedTimestamp || 0) - (a.lastModifiedTimestamp || 0),
        );
      return {
        lockboxes,
        idMap,
      };
    },
  );

  prepareLockboxForRender({
    lockbox,
    intl,
  }: {
    lockbox: TTransformedLockbox;
    intl: TSdkIntlShape;
  }): TDisplayLockbox {
    return {
      ...lockbox,
      formatted: {
        ...lockbox?.formatted,
        controlArrangement:
          lockbox?.formatted?.formatControlArrangement?.(intl),
      },
    };
  }

  filterBalances(
    balances: TInstrumentBalance[],
    instruments: Map<string, Instrument>,
    contracts: { idMap: Map<string, TContract> } | null | undefined,
    entityId: string | undefined,
    {
      includeLiabilities,
      removeZeroBalances,
      includeInactive,
      includeMoney,
      alwaysAddInstrumentIds = [],
      sortOrder = ['instrumentTypeGroup', 'instrumentType', 'instrumentName'],
    }: TBalancesQueryOptions = {},
  ): TFilteredBalance[] {
    const intl = this.getIntl();

    const balanceResults = balances.map((balance) => {
      const instrumentId = balance.instrumentId;
      const instrument = instrumentId
        ? instruments.get(instrumentId)
        : undefined;
      const instrumentDetails = instrument?.instrumentDetails;

      const instrumentType = instrument?.instrumentType;
      const contract = instrument?.contractAddress
        ? contracts?.idMap.get(instrument.contractAddress)
        : undefined;

      // Filter on active/inactive
      const isActive = !instrumentDetails || !!instrumentDetails?.isActive;
      const isExpired = isInstrumentExpired(instrumentDetails, contract);
      // Filter on instrument/liability
      const isLiability = !!instrument?.isLiabilityForEntity(entityId);

      if (instrumentId && !alwaysAddInstrumentIds.includes(instrumentId)) {
        if (
          (includeInactive === true && isActive && !isExpired) ||
          (includeInactive === false && (!isActive || isExpired))
        ) {
          // If includeInactive is undefined or null, don’t filter
          return null;
        }

        // If includeLiabilities is undefined or null, don’t filter
        if (
          (includeLiabilities === true && !isLiability) ||
          (includeLiabilities === false && isLiability)
        ) {
          return null;
        }

        if (balance.totalUnits <= 0) {
          if (removeZeroBalances === true) {
            return null;
          } else if (removeZeroBalances !== false) {
            // If removeZeroBalances isn’t specified, then
            // match the previous behavior: include liabilities with
            // zero balance, but not assets
            if (!isLiability) {
              return null;
            }
          }
        }

        // Filter on money/token
        // If includeMoney is undefined or null, don’t filter
        if (includeMoney === !!instrument?.isMoney) {
          return null;
        }
      }

      const quantities: TFilteredBalance['quantities'] = {};
      if (typeof balance.totalUnits === 'number') {
        quantities.balance = {
          instrumentId: instrument?.instrumentId || instrumentId,
          quantity: balance.totalUnits,
        };
      }
      if (typeof balance.availableUnits === 'number') {
        quantities.available = {
          instrumentId: instrument?.instrumentId || instrumentId,
          quantity: balance.availableUnits,
        };
      }
      if (
        typeof balance.totalUnits === 'number' &&
        typeof balance.availableUnits === 'number' &&
        balance.totalUnits - balance.availableUnits > 0
      ) {
        quantities.reserved = {
          instrumentId: instrument?.instrumentId || instrumentId,
          quantity: balance.totalUnits - balance.availableUnits,
        };
      }
      if (typeof instrumentDetails?.authorizedQuantity === 'number') {
        quantities.authorizedQuantity = {
          instrumentId: instrument?.instrumentId || instrumentId,
          quantity: instrumentDetails.authorizedQuantity,
        };
      }
      if (isLiability) {
        if (
          typeof instrumentDetails?.authorizedQuantity === 'number' &&
          typeof balance.totalUnits === 'number'
        ) {
          quantities.issuedLiability = {
            instrumentId: instrument?.instrumentId || instrumentId,
            quantity: Math.max(
              0,
              instrumentDetails.authorizedQuantity - balance.totalUnits,
            ),
          };
        }
      }

      const balanceResult: TFilteredBalance = {
        instrumentId,
        instrumentName: instrument?.formatInstrumentName(),
        instrumentType: {
          code: instrumentType,
          formatted: formatInstrumentTypeName(instrumentType, intl),
        },
        instrument,
        isActive,
        isExpired,
        isLiability,
        quantities,
      };
      if (contract) {
        balanceResult.contract = {
          address: contract.contractAddress,
          formatted: contract.name,
        };
      }
      if (instrumentDetails?.issuerId) {
        balanceResult.issuer = {
          entityId: instrumentDetails.issuerId,
          formatted: instrument?.formatIssuerName(),
        };
      }
      return balanceResult;
    });

    return balanceResults
      .filter((balance): balance is TFilteredBalance => !!balance)
      .sort((a, b) => {
        for (const order of sortOrder || []) {
          const comparison = balanceOrders[order]?.(a, b) || 0;
          if (comparison !== 0) {
            return comparison;
          }
        }
        return 0;
      });
  }

  balancesQuery = this.makeQuery(
    (query, profileId?: string) => ({
      ...query,
      // Refetch balances every minute
      refetchInterval: minutes(1),
      staleTime: seconds(45),
      enabled: query.enabled && !!profileId,
    }),
    async (context, profileId?: string) => {
      if (!profileId) {
        return null;
      }
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'instrument-balances'),
      );
      return schema.normalizeResponse(
        'Query instrument balances',
        response?.data?.data,
        schema.arrayOf(schemaLockboxBalanceList),
      ).normalizedValue;
    },
  );

  netEquityHistoryQuery = this.makeQuery(
    (
      query,
      { profileId }: { profileId?: string; startDaysAgo?: number } = {},
    ) => ({
      ...query,
      refetchInterval: minutes(5),
      enabled: query.enabled && !!profileId,
    }),
    async (
      context,
      {
        profileId,
        startDaysAgo = 370,
      }: { profileId?: string; startDaysAgo?: number } = {},
    ) => {
      if (!profileId) {
        return null;
      }

      const now = Math.round(Date.now() / 1000);
      const startDate = Math.round(now - startDaysAgo * 86400);

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'vault-balances'),
        {
          params: {
            'filter[start_date]': startDate,
            'filter[end_date]': now,
          },
        },
      );
      const history = schema.normalizeResponse(
        'Query instrument balances',
        response?.data?.data,
        restApiNetEquityHistory,
      ).normalizedValue;

      return history;
    },
  );

  receiveBSVAddressQuery = this.makeQuery(
    (query, profileId?: string, lockboxId?: string) => ({
      ...query,
      staleTime: 0,
      enabled: query.enabled && !!profileId && !!lockboxId,
    }),
    async (context, profileId?: string, lockboxId?: string) => {
      if (!profileId || !lockboxId) {
        return null;
      }
      const address = schema.normalizeResponse(
        'Query address',
        (
          await this.tokenizedApi.endpoints.get(
            urlPath('profiles', profileId, 'lock_boxes', lockboxId, 'address'),
          )
        ).data.data,
        schema.object<{ address?: string }>({ address: schema.string() }),
      ).normalizedValue?.address;
      return address;
    },
  );

  onActivity(profileId?: string) {
    if (profileId) {
      this.balancesQuery.invalidate(profileId);
    } else {
      // Update all profiles if we don’t know which one it was
      this.balancesQuery.invalidate();
    }
    this.instrumentDetailsQuery.invalidate();
  }

  allExchangeRatesQuery = this.makeQuery(
    (query) => ({
      ...query,
      // Don’t re-query for 30 mins (exchange rates are updated
      // hourly by the API)
      staleTime: minutes(30),
      cacheTime: minutes(30),
      enabled: query.enabled,
      retryOnMount: false,
    }),
    async () => {
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('rates', 'all'),
      );
      const rates = schema.normalizeResponse(
        'Query exchange rate',
        response?.data?.data,
        restApiCurrencyExchangeRates,
      ).normalizedValue;

      if (!rates) {
        throw new TokenizedResponseInvalidError('Exchange rates missing');
      }

      return new ExchangeRates(rates);
    },
  );

  exchangeRateQuery = this.makeQuery(
    (query, from?: string | null, to?: string | null) => ({
      ...query,
      // Don’t re-query for 30 mins (exchange rates are updated
      // hourly by the API)
      staleTime: minutes(30),
      enabled: query.enabled && !!from && !!to,
      retryOnMount: false,
    }),
    async (context, from?: string | null, to?: string | null) => {
      if (!from || !to) {
        return null;
      }
      if (from === to) {
        return {
          rate: 1,
        } as TCurrencyExchangeRate;
      }
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('rates', 'currency', from, to),
      );
      return schema.normalizeResponse(
        'Query exchange rate',
        response?.data?.data,
        restApiCurrencyExchangeRate,
      ).normalizedValue;
    },
  );

  instrumentDetailsQuery = this.makeQuery(
    (query, instrumentId?: string | null) => ({
      ...query,
      refetchInterval: minutes(5),
      staleTime: minutes(5),
      enabled: query.enabled && !!instrumentId,
      notifyOnChangeProps: ['data'],
    }),
    async (context, instrumentId?: string | null) => {
      if (!instrumentId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('contracts', 'instruments', instrumentId),
      );
      return schema.normalizeResponse(
        'Query instrument details',
        response?.data?.data,
        restApiInstrumentDetails,
      ).normalizedValue;
    },
  );
}

export default Treasury;
